import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CartItem, MenuItem } from '../types';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface CartCtx {
  cart: CartItem[];
  favorites: number[];
  cartExtras: Record<string, number>;
  addToCart: (item: MenuItem, qty?: number) => void;
  removeFromCart: (itemId: number) => void;
  updateQty: (itemId: number, qty: number) => void;
  clearCart: () => void;
  clearCartExtras: () => void;
  changeCartExtra: (id: string, delta: number) => void;
  // Returns false (and does nothing) when there's no logged-in account — the
  // caller is responsible for sending the user to log in in that case.
  toggleFavorite: (itemId: number) => boolean;
  // Drops any cart items that don't belong to this restaurant — the cart is a single
  // localStorage key shared across every restaurant a visitor browses in the same
  // browser, so without this a leftover item from restaurant A silently rides along
  // into restaurant B's cart/checkout (wrong dish, wrong price, wrong kitchen).
  pruneToRestaurant: (restaurantId: string) => void;
  cartTotal: number;
  cartCount: number;
  extrasCount: number;
}

const CartContext = createContext<CartCtx>({} as CartCtx);
export const useCart = () => useContext(CartContext);

function loadLS<T>(key: string, fallback: T): T {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [cart, setCart]           = useState<CartItem[]>(() => loadLS('cart_v2', []));
  const [favorites, setFavorites] = useState<number[]>([]);
  const [cartExtras, setCartExtras] = useState<Record<string, number>>({});

  useEffect(() => {
    try { localStorage.setItem('cart_v2', JSON.stringify(cart)); } catch {}
  }, [cart]);

  // Favorites require a logged-in account (no more anonymous/localStorage-only
  // likes — those never showed up for anyone else and couldn't be counted
  // reliably). On login, load the account's favorites from the DB and fold in
  // anything liked locally before this account existed (a one-time migration
  // so old pre-login likes aren't lost); logged-out visitors simply have no
  // favorites to show.
  useEffect(() => {
    if (!user) { setFavorites([]); return; }
    (async () => {
      const { data } = await supabase.from('user_favorites').select('item_id').eq('user_id', user.id);
      const serverIds = (data || []).map(r => r.item_id);
      const localIds = loadLS<number[]>('favorites_v2', []);
      const missing = localIds.filter(id => !serverIds.includes(id));
      if (missing.length) {
        await supabase.from('user_favorites').upsert(missing.map(item_id => ({ user_id: user.id, item_id })));
      }
      setFavorites([...serverIds, ...missing]);
      try { localStorage.removeItem('favorites_v2'); } catch {}
    })();
  }, [user]);

  const addToCart = useCallback((item: MenuItem, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(c => c.item.id === item.id);
      if (existing) return prev.map(c => c.item.id === item.id ? { ...c, qty: c.qty + qty } : c);
      return [...prev, { item, qty }];
    });
  }, []);

  const removeFromCart = useCallback((itemId: number) => {
    setCart(prev => prev.filter(c => c.item.id !== itemId));
  }, []);

  const updateQty = useCallback((itemId: number, qty: number) => {
    if (qty <= 0) { removeFromCart(itemId); return; }
    setCart(prev => prev.map(c => c.item.id === itemId ? { ...c, qty } : c));
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart([]);
    try { localStorage.removeItem('cart_v2'); } catch {}
  }, []);

  const pruneToRestaurant = useCallback((restaurantId: string) => {
    setCart(prev => {
      const kept = prev.filter(c => c.item.restaurant_id === restaurantId);
      return kept.length === prev.length ? prev : kept;
    });
  }, []);

  const clearCartExtras = useCallback(() => setCartExtras({}), []);

  const changeCartExtra = useCallback((id: string, delta: number) => {
    setCartExtras(prev => {
      const next = Math.max(0, (prev[id] || 0) + delta);
      if (next === 0) {
        const { [id]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: next };
    });
  }, []);

  const toggleFavorite = useCallback((itemId: number): boolean => {
    if (!user) return false;
    setFavorites(prev => {
      const wasFav = prev.includes(itemId);
      supabase.rpc('increment_like_count', { p_item_id: itemId, p_delta: wasFav ? -1 : 1 }).then(() => {});
      const write = wasFav
        ? supabase.from('user_favorites').delete().eq('user_id', user.id).eq('item_id', itemId)
        : supabase.from('user_favorites').upsert({ user_id: user.id, item_id: itemId });
      write.then(() => {});
      return wasFav ? prev.filter(id => id !== itemId) : [...prev, itemId];
    });
    return true;
  }, [user]);

  const cartTotal   = cart.reduce((sum, c) => sum + c.item.price * c.qty, 0);
  const cartCount   = cart.reduce((sum, c) => sum + c.qty, 0);
  const extrasCount = Object.values(cartExtras).reduce((s, v) => s + v, 0);

  return (
    <CartContext.Provider value={{
      cart, favorites, cartExtras,
      addToCart, removeFromCart, updateQty, clearCart, clearCartExtras, changeCartExtra,
      toggleFavorite, pruneToRestaurant, cartTotal, cartCount, extrasCount,
    }}>
      {children}
    </CartContext.Provider>
  );
}
